import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Newspaper, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NewsCard } from "@/components/NewsCard";
import { useSeo } from "@/hooks/useSeo";
import Header from "@/components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function News() {
  const { category } = useParams();
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const label = cats.find((c) => c.key === category)?.label || (category && category.replace(/-/g, " ").toUpperCase());

  useSeo({
    title: `${label || "Crypto, STX & AI Agent"} News | STXOSHI`,
    description: `Latest ${label || "STX, sBTC, Bitcoin DeFi, AI agents, perpetuals, trading bots and yield vault"} news curated by STXOSHI — the Bitcoin-native agentic economy on Stacks.`,
    path: category ? `/news/category/${category}` : "/news",
  });

  useEffect(() => { axios.get(`${API}/news/categories`).then((r) => setCats(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/news`, { params: { category, page } }).then((r) => {
      setItems((prev) => (page === 1 ? r.data.items : [...prev, ...r.data.items]));
      setPages(r.data.pages);
    }).finally(() => setLoading(false));
  }, [category, page]);

  useEffect(() => { setPage(1); window.scrollTo(0, 0); }, [category]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="news-page">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <Link to="/" data-testid="news-back-home" className="inline-flex items-center gap-2 text-sm font-mono text-muted-foreground hover:text-primary">
          <ArrowLeft className="h-4 w-4" /> STXOSHI home
        </Link>
        <div className="mt-8 flex items-center gap-3">
          <Newspaper className="h-8 w-8 text-primary" />
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-wider" data-testid="news-heading">
            {label ? `${label} News` : "STXOSHI NEWS"}
          </h1>
        </div>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          STX, sBTC, Bitcoin DeFi, AI agents, perps, arbitrage bots, yield vaults and Bitcoin Bonds — refreshed every 30 minutes.
        </p>
        <div className="mt-8 flex flex-wrap gap-2" data-testid="news-category-chips">
          <CatChip to="/news" active={!category} label="All" testid="news-cat-all" />
          {cats.filter((c) => c.count > 0).map((c) => (
            <CatChip key={c.key} to={`/news/category/${c.key}`} active={category === c.key} label={`${c.label} (${c.count})`} testid={`news-cat-${c.key}`} />
          ))}
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="news-grid">
          {items.map((it, i) => <NewsCard key={it.slug} item={it} index={i} />)}
        </div>
        {!loading && items.length === 0 && <p className="mt-10 text-muted-foreground" data-testid="news-empty">No stories yet — check back shortly.</p>}
        <div className="mt-10 flex justify-center">
          {loading ? <Loader2 className="h-6 w-6 animate-spin text-primary" data-testid="news-loading" /> : page < pages && (
            <Button onClick={() => setPage((p) => p + 1)} data-testid="news-load-more" className="rounded-full px-8">Load more</Button>
          )}
        </div>
      </div>
    </div>
  );
}

const CatChip = ({ to, active, label, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`rounded-full border px-4 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${active ? "border-primary bg-primary text-black" : "border-white/15 text-muted-foreground hover:border-primary/60 hover:text-primary"}`}
  >
    {label}
  </Link>
);
